import React, {FC, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {isHtmlElement} from '../../../libs/shared/core/src/helpers/browser/domHelpers';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis, flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';

const StyledHiddenResourcesLabel = styled.div`
  margin-left: 4px;
`;

const StyledIconDiv = styled.div<{$shouldShrink?: boolean}>`
  flex-shrink: 0;
  margin-right: ${(p) => (p.$shouldShrink ? '4px' : '8px')};
`;

const StyledValue = styled.div`
  ${ellipsis()};
`;

const StyledWrapper = styled.div`
  position: relative;
  ${flexCenter}
`;

export interface FilterContentProps {
  children: ReadonlyArray<ReactNode>;
  iconName: IconName | undefined;
  shouldShrink?: boolean;
  iconColor?: string;
}
/*
 * Maximum number of items to display and compute the count of hidden items.
 * Used to avoid rendering way too many items in the filter button.
 */
const MAX_DISPLAYED = 10;

export const FilterContent: FC<FilterContentProps> = ({children, iconName, shouldShrink, iconColor}) => {
  const refParent = useRef<HTMLDivElement | null>(null);
  const [overflowedElements, setOverflowedElements] = useState(0);

  const childrenToDisplay = children.slice(0, MAX_DISPLAYED);

  useLayoutEffect(() => {
    const parentElement = refParent.current;
    if (!parentElement) {
      return;
    }

    const parentWidth = parentElement.offsetWidth;

    let tempOverflowedElements = 0;
    for (const childElement of Array.from(parentElement.children)) {
      if (isHtmlElement(childElement) && childElement.offsetLeft >= parentWidth) {
        tempOverflowedElements++;
      }
    }

    const nonDisplayedOverflowedElements = Math.max(0, children.length - MAX_DISPLAYED);

    setOverflowedElements(tempOverflowedElements + nonDisplayedOverflowedElements);
  }, [children.length, refParent]);

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledWrapper>
          {iconName && (
            <StyledIconDiv $shouldShrink={shouldShrink}>
              <Icon name={iconName} color={iconColor ?? theme.greys.shade70} />
            </StyledIconDiv>
          )}
          <StyledValue ref={refParent}>{childrenToDisplay}</StyledValue>
          {overflowedElements > 0 && (
            <StyledHiddenResourcesLabel>+ {overflowedElements}</StyledHiddenResourcesLabel>
          )}
        </StyledWrapper>
      )}
    </UserThemeConsumer>
  );
};
